/**
 * "Signup return pending" flag: set by `completeAuth` when a new trial signup
 * heads into onboarding, cleared in `redirect()`. `OnboardingView` reads it to
 * tell the first pass through the survey (hand off as a new account) from a
 * return visit (hand off as a plain login).
 */
export const AUTH_EMBEDDED_RETURN_KEY = 'auth_embedded_return';

/**
 * Whether the auth screens are running inside an embedded context (an iframe or
 * a wrapping shell) rather than a regular top-level browser tab.
 *
 * Google blocks OAuth / Google Identity Services inside embedded webviews and
 * frames (`disallowed_useragent`), so SSO UI must hide itself here. The signal
 * is `window.self !== window.top` — the app is framed (the legacy desktop shell
 * loads accounts in an iframe; the pre-Vue app used the same check as its
 * `inDesktop` detection). Cross-origin framing makes reading `window.top`
 * throw, which equally means "framed", so the catch returns true.
 *
 * `AUTH_EMBEDDED_RETURN_KEY` is deliberately NOT a signal here: it means
 * "signup return pending", not "embedded" — `completeAuth` sets it for every
 * new-user trial signup, top-level tabs included. Treating it as embedded hid
 * the SSO buttons from a normal tab that came back to the auth views
 * mid-onboarding, leaving a Google-only (passwordless) account with no way to
 * log in from that tab.
 */
export function isEmbeddedAuthContext(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export function setAuthEmbeddedReturn() {
  sessionStorage.setItem(AUTH_EMBEDDED_RETURN_KEY, '1');
}

export function peekAuthEmbeddedReturn() {
  return sessionStorage.getItem(AUTH_EMBEDDED_RETURN_KEY) === '1';
}

export function clearAuthEmbeddedReturn() {
  sessionStorage.removeItem(AUTH_EMBEDDED_RETURN_KEY);
}
